'use client'

import { FC, PropsWithChildren, useState } from 'react'
import {
  AuthData,
  authTokensStorageKey,
  initialAuthData,
  initialContext,
  SignatureData,
} from './model.ts'
import { createContext, useContext, useContextSelector } from 'use-context-selector'
import { useLocalStorage } from '@hooks/useWebStorage.hook.ts'
import { BannedUserModal } from '@components/shared/banned-user-modal/BannedUserModal.tsx'
import get from 'lodash/get'

const AuthContext = createContext(initialContext)

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const auth = useLocalStorage<AuthData>(authTokensStorageKey, initialAuthData)

  const [isBannedUserOpen, setIsBannedUserOpen] = useState<boolean>(false)

  const [signatureData, setSignatureData] = useState<SignatureData>({
    signature: '',
    message: '',
  })

  return (
    <AuthContext.Provider
      value={{
        auth,

        setIsBannedUserModalOpen: setIsBannedUserOpen,
        signatureData,
        setSignatureData,
      }}
    >
      {children}
      {isBannedUserOpen && <BannedUserModal onClose={() => setIsBannedUserOpen(false)} />}
    </AuthContext.Provider>
  )
}

export const useAuthContext = () => useContext(AuthContext)

export const useIsAuthenticated = () => {
  return useContextSelector(AuthContext, ({ auth: [authData] }) => {
    const emailVerified = get(authData, 'user.emailVerified')
    const uuid = get(authData, 'user.uuid')
    const result = !!(emailVerified && uuid)

    return result
  })
}

export const useAuth = () => useContextSelector(AuthContext, ({ auth }) => auth)

export const useAuthSigniature = () =>
  useContextSelector(AuthContext, ({ signatureData, setSignatureData }) => ({
    signatureData,
    setSignatureData,
  }))
